import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Autocomplete, Box, Button, Checkbox, CircularProgress, Grid, IconButton, InputAdornment, MenuItem, Paper, Radio, TextField, Typography, Alert } from "@mui/material";
import ConfigurableValues from "../../config/constants";
import { searchAddresses } from "../../utils/mapbox";
import { Icon, T } from "./signupDesign";

export const BRAND = T.red;
export const BRAND_HOVER = T.redHover;

export const brandCheckboxSx = { color: T.inputHover, "&.Mui-checked": { color: T.red } };

/**
 * Which step the wizard is on, for the card's eyebrow ("Step 2 of 8 · Legal
 * entity"). Set by PartnerSignup; absent elsewhere (the phone upload page).
 * formStep: a step of fields, which gets "All fields are required unless
 * marked optional."
 */
export const SignupStepContext = createContext(null);

export function StepCard({ title, subtitle, children }) {
  const step = useContext(SignupStepContext);
  return (
    <Paper
      elevation={0}
      sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: "20px", p: "var(--rmf-card-pad, 20px)", background: T.card, position: "relative" }}
    >
      {step && (
        <Typography sx={{ color: T.redText, fontSize: 13, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", mb: 1 }}>
          Step {step.number} of {step.total} · {step.name}
        </Typography>
      )}
      <Typography
        component="h2"
        sx={{ fontFamily: T.display, fontWeight: 700, letterSpacing: "-0.02em", fontSize: { xs: 26, sm: 32 }, lineHeight: 1.15, color: T.text, mb: subtitle ? 1 : 3 }}
      >
        {title}
      </Typography>
      {subtitle && <Typography sx={{ color: T.text2, fontSize: 16, lineHeight: 1.55, mb: step && step.formStep ? 0.75 : 3 }}>{subtitle}</Typography>}
      {step && step.formStep && (
        <Typography sx={{ color: T.muted, fontSize: 14, mb: 3 }}>All fields are required unless marked optional.</Typography>
      )}
      {children}
    </Paper>
  );
}

/** A section title inside a card; every one after the first gets a divider above. */
export function SubHeading({ children, sx, first }) {
  return (
    <Typography
      component="h3"
      sx={{
        fontWeight: 700,
        fontSize: 17,
        color: T.text,
        mb: 2,
        ...(first ? { mt: 0.5 } : { borderTop: `1px solid ${T.divider}`, pt: 3, mt: 1.5 }),
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

/** A stable id for a field name ("owners.0.first" → "rmf-owners-0-first"). */
export const fieldId = (name) => `rmf-${String(name).replace(/[^A-Za-z0-9_-]+/g, "-")}`;

/** The persistent label above an input; optional fields say so. */
export function FieldLabel({ id, htmlFor, label, optional, sx }) {
  return (
    <Box component="label" id={id} htmlFor={htmlFor} sx={{ display: "block", fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.35, mb: 0.75, ...sx }}>
      {label}
      {optional && <Box component="span" sx={{ fontWeight: 400, color: T.muted }}> (optional)</Box>}
    </Box>
  );
}

// "Suite / unit (optional)" or a field that isn't required → label + optional tag.
function splitOptional(label, required) {
  const text = String(label == null ? "" : label);
  const tagged = /\s*\(optional\)\s*$/i.test(text);
  return { text: text.replace(/\s*\(optional\)\s*$/i, ""), optional: tagged || !required };
}

/** A labelled text input; `errors[name]` shows under it. */
export function Field({ label, name, value, onChange, errors = {}, required, xs = 12, sm, md, helperText, select, inputProps, SelectProps, InputLabelProps, ...rest }) {
  const error = errors[name];
  const id = fieldId(name);
  const { text, optional } = splitOptional(label, required);
  return (
    <Grid item xs={xs} sm={sm || xs} md={md}>
      <FieldLabel id={`${id}-label`} htmlFor={id} label={text} optional={optional} />
      <TextField
        fullWidth
        size="small"
        id={id}
        select={select}
        value={value == null ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        error={!!error}
        helperText={error || helperText}
        inputProps={{ "aria-required": required ? true : undefined, ...inputProps }}
        SelectProps={select ? { labelId: `${id}-label`, ...SelectProps } : SelectProps}
        {...rest}
      />
    </Grid>
  );
}

export function StateField({ states, label = "State", ...props }) {
  return (
    <Field select label={label} {...props}>
      <MenuItem value="">Select…</MenuItem>
      {(states || []).map((s) => (
        <MenuItem key={s} value={s}>{s}</MenuItem>
      ))}
    </Field>
  );
}

/**
 * Street address that suggests real addresses as you type (Mapbox, the same
 * service as the rest of the site); picking one fills the city, state and ZIP
 * too. Typing an address by hand still works, and with no map token it is a
 * plain text field.
 */
function StreetAddressField({ label, name, value, onChange, onPick, errors = {} }) {
  const { MAPBOX_TOKEN } = ConfigurableValues();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const latest = useRef("");
  useEffect(() => () => clearTimeout(timer.current), []);

  const search = (text) => {
    clearTimeout(timer.current);
    latest.current = text;
    if (!MAPBOX_TOKEN || String(text).trim().length < 3) {
      setOptions([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const found = await searchAddresses(text, MAPBOX_TOKEN);
        // Only the answer to what is in the box now.
        if (latest.current === text) setOptions(found);
      } catch (e) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const error = errors[name];
  const id = fieldId(name);
  return (
    <Grid item xs={12}>
      <FieldLabel id={`${id}-label`} htmlFor={id} label={label} />
      <Autocomplete
        freeSolo
        id={id}
        options={options}
        // Mapbox already matched them; don't filter again on the typed text.
        filterOptions={(x) => x}
        getOptionLabel={(o) => (typeof o === "string" ? o : o.address1)}
        inputValue={value || ""}
        onInputChange={(e, text, reason) => {
          if (reason === "input") {
            onChange(text);
            search(text);
          } else if (reason === "clear") {
            onChange("");
            setOptions([]);
          }
        }}
        onChange={(e, picked) => {
          if (picked && typeof picked === "object") onPick(picked);
        }}
        loading={loading}
        noOptionsText="Keep typing your street address"
        renderOption={(props, o) => (
          <Box component="li" {...props} key={o.id} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start !important" }}>
            <Box sx={{ color: T.placeholder, mt: "2px" }}><Icon.Pin size={18} /></Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: T.text }}>{o.address1}</Typography>
              <Typography variant="caption" sx={{ color: T.hint }}>
                {[o.city, [o.state, o.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            size="small"
            error={!!error}
            helperText={error || "Start typing and pick your address. Physical address, no P.O. boxes."}
            inputProps={{ ...params.inputProps, autoComplete: "address-line1", "aria-required": true }}
          />
        )}
      />
    </Grid>
  );
}

/** Address block with field names prefixed for owner errors ("owners.0."). */
export function AddressFields({ value, onChange, errors, prefix = "", states, label = "Street address" }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  // A picked suggestion fills the rest; a part it doesn't know keeps what was there.
  const pick = (a) => onChange({
    ...value,
    address1: a.address1,
    city: a.city || value.city,
    state: a.state && (!states || states.includes(a.state)) ? a.state : value.state,
    zip: a.zip || value.zip,
  });
  return (
    <>
      <StreetAddressField label={label} name={`${prefix}address1`} value={value.address1} onChange={set("address1")} onPick={pick} errors={errors} />
      <Field label="Suite / unit (optional)" name={`${prefix}address2`} value={value.address2} onChange={set("address2")} errors={errors} inputProps={{ autoComplete: "address-line2" }} />
      <Field label="City" name={`${prefix}city`} value={value.city} onChange={set("city")} errors={errors} required xs={12} sm={5} inputProps={{ autoComplete: "address-level2" }} />
      <StateField states={states} name={`${prefix}state`} value={value.state} onChange={set("state")} errors={errors} required xs={6} sm={4} />
      <Field label="ZIP" name={`${prefix}zip`} value={value.zip} onChange={set("zip")} errors={errors} required xs={6} sm={3} inputProps={{ inputMode: "numeric", maxLength: 10, autoComplete: "postal-code" }} />
    </>
  );
}

export function ErrorBanner({ error, details }) {
  if (!error) return null;
  const missing = details && Array.isArray(details.missing) ? details.missing : null;
  return (
    <Alert severity="error" icon={<Icon.Alert size={20} />} sx={{ mb: 3 }}>
      {error}
      {missing && (
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
          {missing.map((m) => <li key={m.message}>{m.message}</li>)}
        </Box>
      )}
    </Alert>
  );
}

// ── Buttons ───────────────────────────────────────────────────────────────────

const disabledLook = { background: T.disabledBg, color: T.disabledText };

/** The red pill: 50px, an arrow after the label. */
export function PrimaryButton({ children, busy, disabled, onClick, arrow = true, sx, ...rest }) {
  return (
    <Button
      onClick={onClick}
      disabled={busy || disabled}
      sx={{
        background: T.red, color: "#fff", minHeight: 50, px: 3.5, borderRadius: 999, fontSize: 16, fontWeight: 600, gap: 1,
        "&:hover": { background: T.redHover }, "&.Mui-disabled": disabledLook, ...sx,
      }}
      {...rest}
    >
      {busy ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : (
        <>
          {children}
          {arrow && <Icon.ArrowRight size={18} />}
        </>
      )}
    </Button>
  );
}

/** Transparent pill with a light border (Back). */
export function GhostButton({ children, sx, ...rest }) {
  return (
    <Button
      sx={{ color: T.text, border: `1px solid ${T.input}`, background: "transparent", minHeight: 44, px: 2.5, borderRadius: 999, fontWeight: 600, "&:hover": { background: T.fill, borderColor: T.inputHover }, ...sx }}
      {...rest}
    >
      {children}
    </Button>
  );
}

/** Outlined red pill (Upload, Review the agreement). */
export function OutlineButton({ children, sx, ...rest }) {
  return (
    <Button
      sx={{
        color: T.red, border: `1.5px solid ${T.red}`, background: "#fff", minHeight: 44, px: 2.5, borderRadius: 999, fontWeight: 600, gap: 1, whiteSpace: "nowrap",
        "&:hover": { background: T.redTint, borderColor: T.redHover, color: T.redHover }, "&.Mui-disabled": { ...disabledLook, borderColor: T.disabledBg }, ...sx,
      }}
      {...rest}
    >
      {children}
    </Button>
  );
}

/** Required / Optional / Any one. */
export function Badge({ kind = "optional", children }) {
  const look = {
    required: { background: T.redTint, color: T.redText },
    optional: { background: T.neutralBadgeBg, color: T.neutralBadgeText },
    any: { background: "#FDF1E4", color: "#7A4A0E" },
    done: { background: T.greenTint, color: T.green },
  }[kind];
  return (
    <Box component="span" sx={{ ...look, borderRadius: 999, px: 1.25, py: 0.25, fontSize: 12.5, fontWeight: 700, lineHeight: 1.6, whiteSpace: "nowrap" }}>
      {children}
    </Box>
  );
}

/**
 * The light bar at the bottom of a card: Back (or a note) on the left, the
 * main action on the right. On phones it sticks to the bottom of the screen,
 * with the action full width and the note under it. `hint` is a note about
 * the action itself: under it on a computer, in place of the note on a phone.
 */
export function CardFooter({ left, right, note = "Saved automatically as you go", hint }) {
  return (
    <Box
      sx={{
        mt: 4,
        mx: "calc(-1 * var(--rmf-card-pad, 20px))",
        mb: "calc(-1 * var(--rmf-card-pad, 20px))",
        px: "var(--rmf-card-pad, 20px)",
        py: { xs: 1.5, sm: 2.5 },
        background: T.footerBg,
        borderTop: `1px solid ${T.divider}`,
        borderRadius: { xs: 0, sm: "0 0 20px 20px" },
        position: { xs: "sticky", sm: "static" },
        bottom: 0,
        zIndex: 3,
        boxShadow: { xs: "0 -8px 20px rgba(28,25,23,.06)", sm: "none" },
      }}
    >
      <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: hint ? "flex-start" : "center", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>{left || <Typography sx={{ color: T.muted, fontSize: 14 }}>{note}</Typography>}</Box>
        <Box sx={{ textAlign: "right" }}>
          {right}
          {hint && <Typography sx={{ color: T.muted, fontSize: 13, mt: 1, maxWidth: 360, ml: "auto" }}>{hint}</Typography>}
        </Box>
      </Box>
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <Box sx={{ "& .MuiButton-root": { width: "100%" } }}>{right}</Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: left ? "space-between" : "center", gap: 1.5, mt: 1 }}>
          {left}
          <Typography sx={{ color: T.muted, fontSize: 13, textAlign: left ? "right" : "center" }}>{hint || note}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function StepActions({ onBack, onNext, nextLabel = "Save & continue", busy, disabled, note }) {
  return (
    <CardFooter
      note={note}
      left={onBack ? <GhostButton onClick={onBack} disabled={busy} startIcon={<Icon.ArrowLeft size={16} />}>Back</GhostButton> : null}
      right={<PrimaryButton onClick={onNext} busy={busy} disabled={disabled}>{nextLabel}</PrimaryButton>}
    />
  );
}

/**
 * A checkbox (or radio) as a bordered tile: the whole tile is the label.
 * Behaviour is the control's own; this only frames it.
 */
export function OptionTile({ checked, onChange, value, label, description, radio, error, disabled, sx }) {
  const Control = radio ? Radio : Checkbox;
  return (
    <Box
      component="label"
      sx={{
        display: "flex", gap: 1.5, alignItems: "flex-start", boxSizing: "border-box", p: 2, borderRadius: "14px", background: T.footerBg, cursor: disabled ? "default" : "pointer",
        border: `1px solid ${error ? T.error : checked ? "#E6B8AD" : T.cardBorder}`,
        "&:hover": { borderColor: disabled ? undefined : T.inputHover },
        "&:focus-within": { borderColor: T.red, boxShadow: T.focusRing },
        ...sx,
      }}
    >
      <Control checked={!!checked} onChange={onChange} value={value} disabled={disabled} sx={{ ...brandCheckboxSx, p: 0.25, mt: -0.25 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 15, color: T.text, lineHeight: 1.45 }}>{label}</Typography>
        {description && <Typography sx={{ color: T.hint, fontSize: 14, mt: 0.25, lineHeight: 1.5 }}>{description}</Typography>}
      </Box>
    </Box>
  );
}

/**
 * Before an application is started every step can be opened and read, but
 * not filled in — there is nothing to save it to yet. A disabled fieldset
 * locks every native input, checkbox and button inside; MUI selects are
 * divs, so they are switched off with pointer-events.
 */
export function PreviewLock({ on, children }) {
  if (!on) return children;
  return (
    <Box
      component="fieldset"
      disabled
      sx={{
        border: 0,
        p: 0,
        m: 0,
        minWidth: 0,
        "& .MuiSelect-select": { pointerEvents: "none" },
        "& .MuiInputBase-root": { background: T.fill },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * EIN, SSN, routing and account numbers: shown as dots while typed, with an
 * eye button to check them. A text field masked with CSS rather than a
 * password field — a password field makes the browser offer to save the
 * SSN as a password. Autofill and password managers are switched off.
 */
export function SecretField({ label, name, value, onChange, errors = {}, required, xs = 12, sm, helperText, savedLast4, mask, inputProps = {}, disabled }) {
  const [show, setShow] = useState(false);
  const error = errors[name];
  const id = fieldId(name);
  const { text, optional } = splitOptional(label, required);
  const saved = savedLast4 ? `Saved ${mask}${savedLast4}` : null;
  const note = (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "flex-start", gap: 0.5 }}>
      <Box component="span" sx={{ mt: "1px" }}><Icon.Lock size={13} /></Box>
      <span>
        Encrypted and stored securely
        {saved ? ". Saved; leave blank to keep it." : helperText ? `. ${helperText}` : ""}
      </span>
    </Box>
  );
  return (
    <Grid item xs={xs} sm={sm || xs}>
      <FieldLabel id={`${id}-label`} htmlFor={id} label={text} optional={optional} />
      <TextField
        fullWidth
        size="small"
        id={id}
        disabled={disabled}
        value={value == null ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        error={!!error}
        helperText={error || note}
        placeholder={saved || undefined}
        inputProps={{
          ...inputProps,
          "aria-required": required ? true : undefined,
          autoComplete: "off",
          spellCheck: false,
          "data-lpignore": "true",
          "data-1p-ignore": "true",
          style: !show && value ? { WebkitTextSecurity: "disc" } : undefined,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={show ? `Hide ${text}` : `Show ${text}`}
                onClick={() => setShow((v) => !v)}
                sx={{ width: 44, height: 44, mr: -0.75, color: T.muted }}
              >
                {show ? <Icon.EyeOff size={20} /> : <Icon.Eye size={20} />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Grid>
  );
}

/** A secret the server holds: blank keeps it, the placeholder says so. */
export function secretProps(savedLast4, mask) {
  return savedLast4
    ? { placeholder: `Saved ${mask}${savedLast4}`, helperText: "Saved. Leave blank to keep it.", InputLabelProps: { shrink: true } }
    : {};
}
